import React, { useMemo, useState, useEffect } from 'react';
import { createFilterOptions } from '@mui/material';
import { useQueryClient } from 'react-query';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import clsx from 'clsx';

import { useAuthenticatedState } from 'src/authenticated-state/context';
import Autocomplete from 'src/components/input/autocomplete/Autocomplete';
import AutocompleteMultiple from 'src/components/input/autocomplete-multiple/AutocompleteMultiple';
import type { Option } from 'src/components/input/select/Select';
import CreateOrUpdateLeadModal from 'src/components/create-or-update-lead-modal/CreateOrUpdateLeadModal';
import { Flex } from 'src/components/flex';
import { queryKeys } from 'src/services/api/constants';

import styles from 'src/components/input/select-lead/SelectLead.module.css';

type BaseProps = Omit<React.ComponentProps<typeof Autocomplete>, 'options' | 'value' | 'onChange'>;
type MultipleProps = Omit<React.ComponentProps<typeof AutocompleteMultiple>, 'options' | 'value' | 'onChange'>;

type Props = (BaseProps & {
    allowMultiple?: false;
    value?: string;
    onChange: (value?: string) => void;
}) | (MultipleProps & {
    allowMultiple: true;
    value?: string[];
    onChange: (value?: string[]) => void;
});

const ADD_NEW_VALUE = '__ADD_NEW__';

export default function SelectLead(props: Props) {
    const { value, allowMultiple, onChange, ...restProps } = props;
    const { leads } = useAuthenticatedState();
    const queryClient = useQueryClient();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [previousLeadUuids, setPreviousLeadUuids] = useState<Set<string>>(new Set());

    const options = useMemo(() => {
        const addNewOption: Option<string> = {
            value: ADD_NEW_VALUE,
            label: 'Add New',
        };

        if (!leads) {
            return [addNewOption];
        }

        const filteredLeads = [...leads];

        if (allowMultiple) {
            const selectedLeadUuids = Array.isArray(value) ? value : [];
            const selectedLeads = leads.filter((lead) => selectedLeadUuids.includes(lead.uuid));

            selectedLeads.forEach((selectedLead) => {
                const selectedLeadNotInOptions = !filteredLeads.find((lead) => lead.uuid === selectedLead.uuid);
                if (selectedLeadNotInOptions) {
                    filteredLeads.unshift(selectedLead);
                }
            });
        } else {
            const selectedLead = leads.find((lead) => lead.uuid === value);
            const selectedLeadNotInOptions = !filteredLeads.find((lead) => lead.uuid === selectedLead?.uuid);
            if (selectedLead && selectedLeadNotInOptions) {
                filteredLeads.unshift(selectedLead);
            }
        }

        const leadOptions = filteredLeads
            .map((lead) => ({
                value: lead.uuid,
                label: lead.name,
            }));

        return [addNewOption, ...leadOptions];
    }, [leads, value, allowMultiple]);

    const handleChange = async (newValue: string | string[] | undefined) => {
        if (allowMultiple) {
            const newValueArray = newValue as string[] | undefined;
            if (newValueArray?.includes(ADD_NEW_VALUE)) {
                // Remove the ADD_NEW_VALUE from the selection and open modal
                const filteredValue = newValueArray.filter(v => v !== ADD_NEW_VALUE);
                onChange?.(filteredValue.length > 0 ? filteredValue : undefined);
                setIsCreateModalOpen(true);
            } else {
                onChange?.(newValueArray);
            }
        } else {
            if (newValue === ADD_NEW_VALUE) {
                setIsCreateModalOpen(true);
            } else {
                onChange?.(newValue as string | undefined);
            }
        }
    };

    useEffect(() => {
        if (isCreateModalOpen && leads && previousLeadUuids.size === 0) {
            setPreviousLeadUuids(new Set(leads.map(lead => lead.uuid)));
        }
    }, [isCreateModalOpen, leads, previousLeadUuids]);

    useEffect(() => {
        if (!isCreateModalOpen && leads && previousLeadUuids.size > 0) {
            const newLeads = leads.filter(lead => !previousLeadUuids.has(lead.uuid));

            if (newLeads.length > 0) {
                const newestLead = newLeads.sort((a, b) =>
                    new Date(b.inserted_at).getTime() - new Date(a.inserted_at).getTime()
                )[0];

                if (newestLead) {
                    if (allowMultiple) {
                        const currentValue = Array.isArray(value) ? value : [];
                        if (!currentValue.includes(newestLead.uuid)) {
                            onChange?.([...currentValue, newestLead.uuid]);
                        }
                    } else {
                        onChange?.(newestLead.uuid);
                    }
                }
            }

            setPreviousLeadUuids(new Set());
        }
    }, [isCreateModalOpen, leads, allowMultiple, value, onChange, previousLeadUuids]);

    const handleCreateLeadSuccess = () => {
        // Invalidate the leads query to refresh the options list
        queryClient.invalidateQueries(queryKeys.leads);
    };

    if (allowMultiple) {
        const selectedValues = Array.isArray(value) ? value : [];
        const { getOptionDisabled, ...otherRestProps } = restProps as MultipleProps;

        return (
            <>
                <AutocompleteMultiple
                    {...otherRestProps}
                    getOptionDisabled={getOptionDisabled}
                    value={value as string[] | undefined}
                    onChange={handleChange as (value?: string[]) => void}
                    options={options}
                    filterOptions={filterOptions}
                    getOptionKey={(option) => option.value || ''}
                    renderOption={(optionProps, option, { selected }) => {
                        const isAddNew = option.value === ADD_NEW_VALUE;
                        const isSelected = selected || selectedValues.includes(option.value || '');
                        const isDisabled = getOptionDisabled ? getOptionDisabled(option) : false;

                        // Prevent clicks on disabled options
                        const disabledOptionProps = isDisabled ? {
                            ...optionProps,
                            onClick: (e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                            },
                            onMouseDown: (e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                            },
                        } : optionProps;

                        return (
                            <li {...disabledOptionProps} key={option.value} className={styles.optionItem} aria-disabled={isDisabled}>
                                <Flex className={styles.optionContainer} sx={{ opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
                                    {/* Plus icon for Add New */}
                                    {isAddNew && (
                                        <AddIcon className={styles.addIcon} />
                                    )}

                                    {!isAddNew && (
                                        <Box className={styles.checkboxContainer}>
                                            <Box
                                                className={clsx(
                                                    styles.checkboxBox,
                                                    isSelected && styles.checkboxBoxSelected
                                                )}
                                            >
                                                {isSelected && (
                                                    <Box
                                                        component="svg"
                                                        className={styles.checkmark}
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                        strokeWidth={3}
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M5 13l4 4L19 7"
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    )}

                                    <span className={clsx('ellipsisText', styles.labelText)}>
                                        {option.label}
                                    </span>
                                </Flex>
                            </li>
                        );
                    }}
                />
                {isCreateModalOpen && (
                    <CreateOrUpdateLeadModal
                        onClose={() => setIsCreateModalOpen(false)}
                        onSave={handleCreateLeadSuccess}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <Autocomplete
                {...(restProps as BaseProps)}
                value={value as string | undefined}
                onChange={handleChange as (value?: string) => void}
                options={options}
                filterOptions={filterOptions}
                getOptionKey={(option) => option.value || ''}
                renderOption={(optionProps, option) => (
                    <li {...optionProps} key={option.value}>
                        <span className="ellipsisText" style={{ display: 'block', width: '100%' }}>
                            {option.label}
                        </span>
                    </li>
                )}
            />
            {isCreateModalOpen && (
                <CreateOrUpdateLeadModal
                    onClose={() => setIsCreateModalOpen(false)}
                    onSave={handleCreateLeadSuccess}
                />
            )}
        </>
    );
}

const filterOptions = createFilterOptions<Option<string | undefined>>({
    limit: 100,
});
